import React from "react";
import { X, ArrowRight, Info, Layers, Sparkles } from "lucide-react";
import { cn } from "../../lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../internal/primitives/tabs";
import type { MapTabbedSidePanelSurfaceProps } from "../../contracts/map-workspace";

export function MapTabbedSidePanelSurface({
  title,
  subtitle,
  description,
  badges = [],
  summary = [],
  headerMode = "default",
  hideDescription = false,
  hideSummary = false,
  tabs,
  defaultTabId,
  actions = [],
  onAction,
  onInspect,
  onClear,
  className,
}: MapTabbedSidePanelSurfaceProps) {
  const firstTabId = tabs[0]?.id ?? "";
  const resolvedDefaultTabId =
    (defaultTabId && tabs.some(tab => tab.id === defaultTabId) ? defaultTabId : null) ?? firstTabId;
  const [activeTabId, setActiveTabId] = React.useState(resolvedDefaultTabId);
  const hasCompactBadges = badges.length > 0;
  // In compact mode without badges the X button moves into the tab bar row — no separate header needed.
  const showCompactHeader = headerMode === "compact" && hasCompactBadges;

  React.useEffect(() => {
    setActiveTabId(resolvedDefaultTabId);
  }, [resolvedDefaultTabId]);

  return (
    <div className={cn("flex h-full flex-col bg-background border-l border-border", className)}>
      {headerMode === "compact" ? (
        showCompactHeader ? (
          <header className="shrink-0 border-b border-border bg-muted/5 px-4 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap gap-1.5">
                {badges.map((badge, index) => (
                  <span
                    key={`${badge}:${index}`}
                    className="rounded-sm bg-primary px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-primary-foreground shadow-sm"
                  >
                    {badge}
                  </span>
                ))}
              </div>
              {onClear ? (
                <button
                  onClick={onClear}
                  aria-label="Close panel"
                  className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </header>
        ) : null
      ) : (
        <header className="shrink-0 border-b border-border bg-muted/5 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-col">
              <h2 className="text-base font-black uppercase tracking-tight leading-none text-foreground">
                {title}
              </h2>
              {subtitle ? (
                <p className="mt-2 text-xs font-bold uppercase tracking-widest text-primary opacity-80">
                  {subtitle}
                </p>
              ) : null}
            </div>
            {onClear ? (
              <button
                onClick={onClear}
                aria-label="Close panel"
                className="rounded-full p-2 -mt-2 -mr-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            ) : null}
          </div>

          {badges.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {badges.map((badge, index) => (
                <span
                  key={`${badge}:${index}`}
                  className="rounded-sm bg-primary px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-primary-foreground shadow-sm"
                >
                  {badge}
                </span>
              ))}
            </div>
          ) : null}
        </header>
      )}

      <div className="flex-1 overflow-y-auto">
        <div className={cn(headerMode === "compact" ? "space-y-4 p-4 pt-3" : "space-y-6 p-6")}>
          {!hideDescription && description ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Info className="h-3.5 w-3.5" />
                <span className="text-[10px] font-black uppercase tracking-widest">Description</span>
              </div>
              <p className="text-sm leading-relaxed text-foreground/80">{description}</p>
            </div>
          ) : null}

          {!hideSummary && summary.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" />
                <span className="text-[10px] font-black uppercase tracking-widest">Key Metrics</span>
              </div>
              <div className="grid grid-cols-1 gap-px overflow-hidden rounded border border-border bg-border">
                {summary.map(item => (
                  <div key={item.key} className="flex items-center justify-between bg-background p-4">
                    <span className="text-xs font-bold uppercase tracking-tight text-muted-foreground">
                      {item.label}
                    </span>
                    <span className="font-mono text-sm font-black text-foreground">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {tabs.length > 0 ? (
            <Tabs
              value={activeTabId}
              onValueChange={setActiveTabId}
              className={cn(headerMode === "compact" ? "space-y-3" : "space-y-4")}
            >
              {/* Tab bar: full-width flex row, X close button inlined when compact+no-badges */}
              <div className={cn("flex items-center gap-1", headerMode !== "compact" && "pb-1")}>
                <TabsList className="flex h-auto flex-1 justify-start gap-1 rounded-lg bg-muted p-1">
                  {tabs.map(tab => (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className="flex-1 h-auto rounded-md px-3 py-2 text-[11px] font-black uppercase tracking-[0.12em]"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {/* Compact+no-badges: X lives here instead of a separate header row */}
                {headerMode === "compact" && !hasCompactBadges && onClear ? (
                  <button
                    onClick={onClear}
                    aria-label="Close panel"
                    className="shrink-0 rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              {tabs.map(tab => (
                <TabsContent
                  key={tab.id}
                  value={tab.id}
                  className={cn("mt-0", headerMode === "compact" ? "pt-0" : undefined)}
                >
                  {tab.content ? (
                    <div className={cn(headerMode === "compact" ? "space-y-3" : "space-y-4")}>
                      {tab.content}
                    </div>
                  ) : (
                    <div className="rounded-md border-2 border-dashed border-border p-6 text-center text-xs font-medium text-muted-foreground opacity-70">
                      No content is available for this tab.
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          ) : null}

          {onInspect ? (
            <button
              onClick={onInspect}
              className="group flex w-full items-center justify-between rounded-lg border border-border bg-muted/20 p-4 transition-all hover:bg-muted/40"
            >
              <div className="flex items-center gap-3">
                <div className="rounded bg-background p-2 border border-border">
                  <Layers className="h-4 w-4 text-primary" />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-xs font-bold text-foreground">Technical Inspection</span>
                  <span className="text-[10px] font-medium text-muted-foreground">
                    View raw properties and source data
                  </span>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
            </button>
          ) : null}
        </div>
      </div>

      {actions.length > 0 ? (
        <footer className="shrink-0 space-y-3 border-t border-border bg-background p-6">
          {actions.map(action => (
            <button
              key={action.id}
              disabled={action.disabled}
              onClick={() => onAction?.(action.id)}
              className={cn(
                "group flex h-12 w-full items-center justify-center gap-2 rounded-md text-sm font-black shadow-sm transition-all",
                "bg-primary text-primary-foreground hover:brightness-110 active:scale-[0.99]",
                "disabled:cursor-not-allowed disabled:grayscale disabled:opacity-50 disabled:active:scale-100",
              )}
            >
              <span>{action.label}</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          ))}
        </footer>
      ) : null}
    </div>
  );
}
